import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { CssBaseline, GlobalStyles } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import store from "./app/store";
import { Provider } from "react-redux";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import loader from "./assets/gif/loading.gif";

import { EmbryoSettingsProvider } from "./component/gameteTab/EmbryoSettingsContext";
import { restoreTabSession } from "./app/services/session";

const App = lazy(() => import("./App"));

/*
 * MUI 5.6 inputs each inject the same two global @keyframes (used to detect browser
 * autofill) as a new <style> tag every time an input mounts. Screens with many inputs
 * mounted hundreds of identical tags, and every new tag makes the browser re-check the
 * styles of the whole page. Inputs skip it here (MUI's own option) and the keyframes are
 * added once, so autofill detection works exactly as before. The theme is MUI's default
 * theme (what every component already used without a provider), so nothing else changes.
 */
const APP_THEME = createTheme({
  components: {
    MuiInputBase: { defaultProps: { disableInjectingGlobalStyles: true } },
    // marks every select's input box (`data-select` on .MuiInputBase-root) so CSS can style
    // select boxes without `:has(.MuiSelect-select)`, which slows style updates page-wide
    MuiSelect: { defaultProps: { "data-select": "" } },
  },
});
const INPUT_AUTOFILL_KEYFRAMES = {
  "@keyframes mui-auto-fill": { from: { display: "block" } },
  "@keyframes mui-auto-fill-cancel": { from: { display: "block" } },
};
const Loader = () => (
  <div
    style={{
      width: "max-content",
      height: "max-content",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
    }}
  >
    <img
      src={loader}
      alt="loading"
      style={{
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        width: "100%",
        height: "300px",
      }}
    />
  </div>
);
// a new tab of a signed-in user gets the login's permission flags before any screen reads them
restoreTabSession();
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Suspense fallback={<Loader />}>
    <React.StrictMode>
      <Provider store={store}>
        <EmbryoSettingsProvider>
          <BrowserRouter>
            <ThemeProvider theme={APP_THEME}>
              <GlobalStyles styles={INPUT_AUTOFILL_KEYFRAMES} />
              <CssBaseline />
              <App />
            </ThemeProvider>
          </BrowserRouter>
        </EmbryoSettingsProvider>
      </Provider>
    </React.StrictMode>
  </Suspense>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
